import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import type { FormData, OptionItem } from "../FormRenderer/types";
import { useAsyncOptions } from "../FormRenderer/useAsyncOptions";
import {
  ensureValuesInOptions,
  getBaseOptionsForField,
} from "../FormRenderer/formFieldUtils";
import type {
  TaskTableDefinition,
  TaskTableRow,
} from "../../types/manualAssessmentWorkflow";
import {
  buildTaskTableCellAsyncFormData,
  findTaskAssigneeSourceColumn,
  findTriageReviewerColumn,
  resolveTaskTableSelectDisplayLabel,
} from "../../utils/manualAssessmentTaskTable";
import {
  normalizeTaskTableAsyncConfig,
  taskTableFieldHasAsyncOptions,
} from "../../utils/taskTableAsyncConfig";
import {
  HOME_LIST_TABLE_CELL_CONTROL_SLOT_SX,
  HOME_LIST_TABLE_INACTIVE_TEXT_SX,
  HOME_LIST_TABLE_TOKENS,
} from "../../styles/homeListTableTokens";

const INACTIVE_CELL_PLACEHOLDER = "—";

export type TaskTableReviewerDisplayProps = Readonly<{
  row: TaskTableRow;
  value: unknown;
  formData?: FormData;
  /** When set, options are fetched from both Assign To SME and SME Leader Review owners. */
  table?: TaskTableDefinition;
}>;

function toStaticOptions(
  field: { options?: Array<{ label: string; value: string | number | boolean }> } | undefined,
): OptionItem[] {
  return (field?.options ?? []).map((option) => ({
    label: option.label,
    value: option.value,
  }));
}

function mergeOptionItems(
  primary: readonly OptionItem[],
  secondary: readonly OptionItem[],
): OptionItem[] {
  const seen = new Set<string>();
  const merged: OptionItem[] = [];
  for (const option of [...primary, ...secondary]) {
    const key = String(option.value);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(option);
  }
  return merged;
}

function formDataForColumn(
  table: TaskTableDefinition | undefined,
  row: TaskTableRow,
  column: ReturnType<typeof findTaskAssigneeSourceColumn>,
  formData: FormData | undefined,
): FormData | undefined {
  if (!table || !column) return formData;
  return buildTaskTableCellAsyncFormData(formData, table, row, column);
}

export function TaskTableReviewerDisplay({
  row,
  value,
  formData,
  table,
}: TaskTableReviewerDisplayProps) {
  const sourceColumn = findTaskAssigneeSourceColumn(row);
  const reviewerColumn = findTriageReviewerColumn(row);
  const sourceField = sourceColumn?.field;
  const reviewerField = reviewerColumn?.field;

  const sourceFormData = formDataForColumn(table, row, sourceColumn, formData);
  const reviewerFormData = formDataForColumn(table, row, reviewerColumn, formData);

  const sourceAsyncConfig = normalizeTaskTableAsyncConfig(sourceField);
  const reviewerAsyncConfig = normalizeTaskTableAsyncConfig(reviewerField);

  const { options: sourceAsyncOptions, loading: sourceLoading } = useAsyncOptions(
    sourceAsyncConfig,
    sourceFormData,
  );
  const { options: reviewerAsyncOptions, loading: reviewerLoading } =
    useAsyncOptions(reviewerAsyncConfig, reviewerFormData);

  const hasValue = value != null && value !== "";
  const isLoading =
    (Boolean(sourceAsyncConfig?.url) && sourceLoading) ||
    (Boolean(reviewerAsyncConfig?.url) && reviewerLoading);

  const label = useMemo(() => {
    const staticOptions = mergeOptionItems(
      toStaticOptions(sourceField),
      toStaticOptions(reviewerField),
    );
    const asyncOptions = mergeOptionItems(sourceAsyncOptions, reviewerAsyncOptions);
    const hasAsync =
      taskTableFieldHasAsyncOptions(sourceField) ||
      taskTableFieldHasAsyncOptions(reviewerField);
    const useAsyncAsBase = hasAsync && asyncOptions.length > 0;
    const base = getBaseOptionsForField(useAsyncAsBase, staticOptions, asyncOptions);
    const options = ensureValuesInOptions(
      base,
      hasValue ? [value] : [],
    );
    return resolveTaskTableSelectDisplayLabel(options, value);
  }, [
    hasValue,
    reviewerAsyncOptions,
    reviewerField,
    sourceAsyncOptions,
    sourceField,
    value,
  ]);

  if (isLoading) {
    return (
      <Box
        role="status"
        aria-label="Loading options"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: HOME_LIST_TABLE_TOKENS.cellControlMinHeight,
          width: "100%",
        }}
      >
        <CircularProgress size={18} />
      </Box>
    );
  }

  return (
    <Box sx={HOME_LIST_TABLE_CELL_CONTROL_SLOT_SX}>
      <Typography
        component="span"
        sx={{
          ...HOME_LIST_TABLE_INACTIVE_TEXT_SX,
          color: label
            ? HOME_LIST_TABLE_TOKENS.cellColor
            : HOME_LIST_TABLE_TOKENS.inactiveColor,
        }}
      >
        {label || INACTIVE_CELL_PLACEHOLDER}
      </Typography>
    </Box>
  );
}

export default TaskTableReviewerDisplay;
